// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Starter charts of accounts for the first-run setup wizard.
//
// Mirror of packages/core/src/coa-templates.ts. Duplicated here (like
// src/types.ts and src/lib/currencies.ts) to avoid a build-time dependency on
// the core package from the browser bundle. Keep the two in sync.
//
// DECISION: templates are declared as plain data (a tree of nodes), not as
// event-construction logic, so they can be audited, extended, or exposed for
// power-user customization later without touching the seeding code path.
//
// DECISION: leaf accounts inherit the wizard's chosen base currency. Grouping
// accounts (any node with children) are marked `placeholder: true` so no
// transaction can be posted directly against them — matching GnuCash header
// accounts. Accounts carry the modern account types (CREDIT_CARD,
// ACCOUNTS_RECEIVABLE, ACCOUNTS_PAYABLE) rather than the legacy aliases.

import { v4 as uuidv4 } from 'uuid';
import type { AccountCreatedPayload, AccountType } from '../types.js';

export type CoaTemplateId = 'personal' | 'business';

export interface CoaTemplateNode {
  name: string;
  type: AccountType;
  /**
   * Contra account (e.g. Accumulated Depreciation): its natural balance runs
   * opposite to its account type. The AccountCreated schema has no
   * normal-balance / contra flag today, so this is advisory metadata only — see
   * the TODO in buildAccountPayloads. Kept in the template so the intent is not
   * lost and can be honored once the schema supports it.
   */
  contra?: boolean;
  children?: CoaTemplateNode[];
}

export interface CoaTemplate {
  id: CoaTemplateId;
  label: string;
  description: string;
  roots: CoaTemplateNode[];
}

// ─── Personal / Family ──────────────────────────────────────────────────────

export const PERSONAL_TEMPLATE: CoaTemplate = {
  id: 'personal',
  label: 'Personal',
  description: 'Everyday household finances: bank accounts, cards, income, and living expenses.',
  roots: [
    {
      name: 'Assets',
      type: 'ASSET',
      children: [
        {
          name: 'Current Assets',
          type: 'ASSET',
          children: [
            { name: 'Checking', type: 'BANK' },
            { name: 'Savings', type: 'BANK' },
            { name: 'Cash', type: 'CASH' },
          ],
        },
        {
          name: 'Investments',
          type: 'ASSET',
          children: [
            { name: 'Brokerage', type: 'STOCK' },
            { name: 'Retirement (401k/IRA)', type: 'STOCK' },
          ],
        },
        {
          name: 'Fixed Assets',
          type: 'ASSET',
          children: [
            { name: 'Vehicles', type: 'ASSET' },
            { name: 'Real Estate', type: 'ASSET' },
          ],
        },
      ],
    },
    {
      name: 'Liabilities',
      type: 'LIABILITY',
      children: [
        { name: 'Credit Cards', type: 'CREDIT_CARD' },
        { name: 'Mortgage', type: 'LIABILITY' },
        { name: 'Auto Loan', type: 'LIABILITY' },
        { name: 'Student Loans', type: 'LIABILITY' },
      ],
    },
    {
      name: 'Equity',
      type: 'EQUITY',
      children: [{ name: 'Opening Balances', type: 'EQUITY' }],
    },
    {
      name: 'Income',
      type: 'INCOME',
      children: [
        { name: 'Salary', type: 'INCOME' },
        { name: 'Interest Income', type: 'INCOME' },
        { name: 'Dividend Income', type: 'INCOME' },
        { name: 'Other Income', type: 'INCOME' },
      ],
    },
    {
      name: 'Expenses',
      type: 'EXPENSE',
      children: [
        {
          name: 'Housing',
          type: 'EXPENSE',
          children: [
            { name: 'Rent/Mortgage Interest', type: 'EXPENSE' },
            { name: 'Utilities', type: 'EXPENSE' },
            { name: 'Home Maintenance', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Transportation',
          type: 'EXPENSE',
          children: [
            { name: 'Fuel', type: 'EXPENSE' },
            { name: 'Auto Insurance', type: 'EXPENSE' },
            { name: 'Maintenance', type: 'EXPENSE' },
            { name: 'Public Transit', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Food',
          type: 'EXPENSE',
          children: [
            { name: 'Groceries', type: 'EXPENSE' },
            { name: 'Dining Out', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Healthcare',
          type: 'EXPENSE',
          children: [
            { name: 'Insurance', type: 'EXPENSE' },
            { name: 'Medical', type: 'EXPENSE' },
            { name: 'Dental', type: 'EXPENSE' },
            { name: 'Pharmacy', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Personal',
          type: 'EXPENSE',
          children: [
            { name: 'Clothing', type: 'EXPENSE' },
            { name: 'Entertainment', type: 'EXPENSE' },
            { name: 'Subscriptions', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Insurance',
          type: 'EXPENSE',
          children: [
            { name: 'Life', type: 'EXPENSE' },
            { name: 'Health', type: 'EXPENSE' },
          ],
        },
        {
          name: 'Taxes',
          type: 'EXPENSE',
          children: [
            { name: 'Federal', type: 'EXPENSE' },
            { name: 'State', type: 'EXPENSE' },
            { name: 'Property', type: 'EXPENSE' },
          ],
        },
        { name: 'Interest Paid', type: 'EXPENSE' },
        { name: 'Charitable Giving', type: 'EXPENSE' },
        { name: 'Miscellaneous', type: 'EXPENSE' },
      ],
    },
  ],
};

// ─── Small Business ─────────────────────────────────────────────────────────

export const BUSINESS_TEMPLATE: CoaTemplate = {
  id: 'business',
  label: 'Business',
  description: 'Small-business books: receivables, payables, payroll, and revenue vs. expenses.',
  roots: [
    {
      name: 'Assets',
      type: 'ASSET',
      children: [
        {
          name: 'Current Assets',
          type: 'ASSET',
          children: [
            { name: 'Business Checking', type: 'BANK' },
            { name: 'Business Savings', type: 'BANK' },
            { name: 'Petty Cash', type: 'CASH' },
            { name: 'Accounts Receivable', type: 'ACCOUNTS_RECEIVABLE' },
          ],
        },
        {
          name: 'Fixed Assets',
          type: 'ASSET',
          children: [
            { name: 'Equipment', type: 'ASSET' },
            { name: 'Furniture & Fixtures', type: 'ASSET' },
            // Contra-asset: carries a negative normal balance against the other
            // fixed assets. Seeded as a plain ASSET for now — see TODO in
            // buildAccountPayloads.
            { name: 'Accumulated Depreciation', type: 'ASSET', contra: true },
          ],
        },
      ],
    },
    {
      name: 'Liabilities',
      type: 'LIABILITY',
      children: [
        { name: 'Accounts Payable', type: 'ACCOUNTS_PAYABLE' },
        { name: 'Business Credit Cards', type: 'CREDIT_CARD' },
        { name: 'Business Loans', type: 'LIABILITY' },
        { name: 'Sales Tax Payable', type: 'LIABILITY' },
        { name: 'Payroll Liabilities', type: 'LIABILITY' },
      ],
    },
    {
      name: 'Equity',
      type: 'EQUITY',
      children: [
        { name: "Owner's Capital", type: 'EQUITY' },
        { name: "Owner's Draws", type: 'EQUITY' },
        { name: 'Retained Earnings', type: 'EQUITY' },
      ],
    },
    {
      name: 'Income',
      type: 'INCOME',
      children: [
        { name: 'Sales Revenue', type: 'INCOME' },
        { name: 'Service Revenue', type: 'INCOME' },
        { name: 'Interest Income', type: 'INCOME' },
        { name: 'Other Income', type: 'INCOME' },
      ],
    },
    {
      name: 'Expenses',
      type: 'EXPENSE',
      children: [
        { name: 'Cost of Goods Sold', type: 'EXPENSE' },
        { name: 'Advertising & Marketing', type: 'EXPENSE' },
        { name: 'Bank Fees', type: 'EXPENSE' },
        { name: 'Insurance', type: 'EXPENSE' },
        { name: 'Office Supplies', type: 'EXPENSE' },
        {
          name: 'Payroll',
          type: 'EXPENSE',
          children: [
            { name: 'Wages', type: 'EXPENSE' },
            { name: 'Payroll Taxes', type: 'EXPENSE' },
          ],
        },
        { name: 'Professional Services (Legal, Accounting)', type: 'EXPENSE' },
        { name: 'Rent', type: 'EXPENSE' },
        { name: 'Software & Subscriptions', type: 'EXPENSE' },
        { name: 'Travel', type: 'EXPENSE' },
        { name: 'Utilities', type: 'EXPENSE' },
        { name: 'Depreciation Expense', type: 'EXPENSE' },
      ],
    },
  ],
};

export const COA_TEMPLATES: Record<CoaTemplateId, CoaTemplate> = {
  personal: PERSONAL_TEMPLATE,
  business: BUSINESS_TEMPLATE,
};

/**
 * Stable i18n key (camelCase slug) for a starter-account name, e.g.
 * "Accounts Receivable" → "accountsReceivable", "Retirement (401k/IRA)" →
 * "retirement401kIra". Used both to build the `onboarding.seedAccounts.*` keys
 * and, at seed time, to translate each name — so the two always match. Seeded
 * names are localized on creation (then become ordinary, editable account data);
 * a missing translation falls back to the English name.
 */
export function seedAccountNameKey(name: string): string {
  const words = name
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      return i === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/** Every distinct starter-account name across all templates (for i18n tooling). */
export function allSeedAccountNames(): string[] {
  const names = new Set<string>();
  const walk = (n: CoaTemplateNode): void => {
    names.add(n.name);
    n.children?.forEach(walk);
  };
  for (const tpl of Object.values(COA_TEMPLATES)) tpl.roots.forEach(walk);
  return [...names];
}

/**
 * Turn a template + base currency into an ordered list of AccountCreated
 * payloads. Parents are always emitted before their children (depth-first
 * pre-order), so appending them in array order yields no orphaned parentIds on
 * replay. Fresh UUIDv4 account ids are generated per call, so seeding the same
 * template twice produces distinct account trees.
 */
export function buildAccountPayloads(
  template: CoaTemplate,
  baseCurrency: string,
  // Localize each account name at seed time (see seedAccountNameKey). Defaults to
  // identity so non-UI callers/tests keep the English names.
  translateName: (name: string) => string = (n) => n,
): AccountCreatedPayload[] {
  const payloads: AccountCreatedPayload[] = [];

  const walk = (node: CoaTemplateNode, parentId: string | null): void => {
    const accountId = uuidv4();
    const isGroup = Array.isArray(node.children) && node.children.length > 0;

    // TODO(contra): node.contra accounts (e.g. Accumulated Depreciation) are
    // contra accounts whose natural balance is opposite their account type. The
    // AccountCreated schema has no normal-balance / contra field yet, so they
    // are seeded as ordinary accounts of their declared type. Honor node.contra
    // here once the schema gains a contra flag.
    payloads.push({
      accountId,
      name: translateName(node.name),
      type: node.type,
      currency: baseCurrency,
      parentId,
      description: '',
      placeholder: isGroup,
    });

    if (node.children) {
      for (const child of node.children) walk(child, accountId);
    }
  };

  for (const root of template.roots) walk(root, null);
  return payloads;
}
